import ParameterValidator from '@mvps-genesis/parameter-validator';
import {MenuBar, MenuItem} from '@mvps-genesis/accessibility';
import {getAbsoluteParent, getNextSibling, getParent} from '@mvps-genesis/selector-helpers';
import {createFocusTrap} from 'focus-trap';

/* eslint-disable no-invalid-this */
export default class NavigationMenu {

	/**
	 * Options:
	 *
	 * options.drawers
	 * options.mainMenu
	 * options.mainMenuOpenButtons
	 * options.mainMenuCloseButtons
	 * options.menuItems
	 * options.parentMenus
	 * options.showPreviousMenuButtons
	 */
	constructor (options) {

		// Create validator
		this._validator = new ParameterValidator(this);

		this.options = this._validator.isObject('options', options);
		this.breakpoint = this._validator.isString('options.breakpoint', options.breakpoint || '75em');

		this.elements = {
			mainMenu: this._validator.isElement('options.mainMenu', options.mainMenu),
			mainMenuCloseButtons: this._validator.isElementList('options.mainMenuCloseButtons', options.mainMenuCloseButtons),
			mainMenuOpenButtons: this._validator.isElementList('options.mainMenuOpenButtons', options.mainMenuOpenButtons),
			parentMenus: this._validator.isElementList('options.parentMenus', options.parentMenus),
			showPreviousMenuButtons: this._validator.isElementList('options.showPreviousMenuButtons', options.showPreviousMenuButtons)
		};
		this.elements.menuItems = this._validator.isElementList('options.menuItems', options.menuItems, {container: this.elements.mainMenu});
		this.elements.parentMenuItems = this._validator.isElementList('parentMenuItems', `${options.parentMenus} > ${options.menuItems}`, {container: this.elements.mainMenu});

		this.accessibility = {
			// Create focus trap for mobile menu
			focusTrap: createFocusTrap(this.elements.mainMenu, {
				escapeDeactivates: false,
				setReturnFocus: this.elements.mainMenuOpenButtons[0]
			}),
			// Create accessible (keyboard navigation) menu from the main NavigationMenu element
			menuBar: new MenuBar({element: this.elements.mainMenu})
		};

		this.bindEvents();

	}

	isMobile () {
		return this.breakpoint ? window.matchMedia(`(max-width: ${this.breakpoint})`).matches : false;
	}

	bindEvents (add = true) {

		const method = add ? 'addEventListener' : 'removeEventListener';

		// Add listener for top-level close
		this.accessibility.menuBar[method](MenuBar.events.close, (e) => this.onAccessibilityMenuClose(e), true);

		// Mobile open and close events
		for (const mainMenuOpenButton of this.elements.mainMenuOpenButtons) {

			mainMenuOpenButton[method]('click', this.mainMenuOpen, false);

		}
		for (const mainMenuCloseButton of this.elements.mainMenuCloseButtons) {

			mainMenuCloseButton[method]('click', this.mainMenuClose, false);

		}

		for (const menuItem of this.elements.menuItems) {

			menuItem[method]('blur', this.onMenuItemBlur, false);
			menuItem[method]('focus', this.onMenuItemFocus, false);

		}

		for (const parentMenuItem of this.elements.parentMenuItems) {

			parentMenuItem[method]('click', this.onParentMenuItemClick, false);
			parentMenuItem[method](MenuBar.events.enterSubMenu, this.onParentMenuItemClick, false);

		}

		// Show previous button
		for (const showPreviousMenuButton of this.elements.showPreviousMenuButtons) {

			showPreviousMenuButton[method]('click', this.onPreviousButtonClick, false);

		}

	}

	destroyEvents () {
		this.bindEvents(false);
	}

	mainMenuOpen = () => {

		document.documentElement.classList.add('has-open-main-menu');
		this.elements.mainMenu.classList.add('is-open');
		this.elements.mainMenu.classList.remove('is-hidden');

		this.accessibility.menuBar.focusFirstMenuItem();
		this.accessibility.focusTrap.activate();

	};

	mainMenuClose = () => {

		document.documentElement.classList.remove('has-open-main-menu');
		this.elements.mainMenu.classList.remove('is-open');
		this.elements.mainMenu.classList.add('is-hidden');

		this.accessibility.menuBar.resetMenuItemsState();
		this.accessibility.focusTrap.deactivate();

	};

	onAccessibilityMenuClose (e) {

		if (!this.isMobile()) {
			return false;
		}

		if (e && e.detail && e.detail.menu) {
			if (e.detail.menu.root) {
				this.mainMenuClose();
			}
			else {
				const previousButton = e.detail.menu.element.querySelector(this.options.showPreviousMenuButtons);
				this.showPreviousMenu(previousButton);
			}

		}

		return true;

	}

	onMenuItemFocus = (e) => {

		if (this.isMobile()) {
			return;
		}

		const parent = getAbsoluteParent(e.target, this.options.parentMenus);

		if (parent) {
			parent.classList.add('is-open');
		}

	};

	onMenuItemBlur = (e) => {

		if (this.isMobile()) {
			return;
		}

		const parent = getAbsoluteParent(e.target, this.options.parentMenus);

		if (parent) {
			parent.classList.remove('is-open');
		}

	};

	onParentMenuItemClick = (e) => {

		e.stopPropagation();

		if (!this.isMobile()) {
			return true;
		}

		e.preventDefault();

		let clickElement = e.target;
		const parentElement = getParent(clickElement, this.options.menuItems);
		if (parentElement) {
			clickElement = parentElement;
		}

		const nextDrawer = getNextSibling(clickElement, this.options.drawers);
		if (nextDrawer) {
			nextDrawer.classList.add('is-open');
		}

		return false;

	};

	onPreviousButtonClick = (e) => {

		e.stopPropagation();
		e.preventDefault();

		const parentDrawer = getParent(e.target, this.options.drawers);
		parentDrawer.classList.remove('is-open');

		// Properly handle correct focusing on "back"
		const menuItem = MenuItem.getInstance(e.target);
		if (menuItem) {
			menuItem.dispatchEvent('keydown', {
				key: 'Escape'
			});
		}

	};

	showPreviousMenu (target) {
		const parentDrawer = getParent(target, this.options.drawers);
		if (parentDrawer) {
			parentDrawer.classList.remove('is-open');
		}
	}

};
